import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ProductCreateDto, ProductDto, ProductService, ProductUpdateDto } from '../services/product.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';

interface ProductDraft {
  id: number;
  name: string;
  sku: string;
  description: string;
  defaultPrice: string | number;
  isTaxable: boolean;
  defaultItem: boolean;
}

@Component({
  selector: 'app-products-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      @if (forbidden) {
        <section class="error-section">
          <h3>Access Denied</h3>
          <p>You do not have access to view products. Please contact your administrator.</p>
        </section>
      }

      @if (!forbidden) {
        <section class="page-hero">
          <div class="page-hero-inner">
            <a routerLink="/home" class="home-btn">Home</a>
            <div class="hero-top-row">
              <div class="hero-icon hero-icon--violet">
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 2h12l4 6-10 14L2 8z"></path>
                  <path d="M6 2l6 20"></path>
                  <path d="M18 2l-6 20"></path>
                  <path d="M2 8h20"></path>
                </svg>
              </div>
              <app-hero-user-pill></app-hero-user-pill>
            </div>
            <h1 class="page-title">Products</h1>
            <p class="page-sub">Manage product catalog records used in invoice flows.</p>
          </div>
        </section>

        <section class="page-body">
          <div class="page-body-inner">
            <div class="card">
              <div class="card-hd">
                <span class="card-title">Create Product</span>
              </div>

              <div class="form-grid">
                <label class="field">
                  <span>Name</span>
                  <input type="text" [(ngModel)]="newProduct.name" />
                </label>

                <label class="field">
                  <span>SKU</span>
                  <input type="text" [(ngModel)]="newProduct.sku" />
                </label>

                <label class="field field--full">
                  <span>Description</span>
                  <input type="text" [(ngModel)]="newProduct.description" />
                </label>

                <label class="field">
                  <span>Default Price</span>
                  <input type="number" step="0.01" [(ngModel)]="newProduct.defaultPrice" />
                </label>

                <label class="field field--checkbox">
                  <span>Taxable</span>
                  <input type="checkbox" [(ngModel)]="newProduct.isTaxable" />
                </label>

                <label class="field field--checkbox">
                  <span>Default Item</span>
                  <input type="checkbox" [(ngModel)]="newProduct.defaultItem" />
                </label>

                <div class="actions field--full">
                  <button class="btn btn--primary" type="button" [disabled]="isSavingCreate" (click)="createProduct()">
                    {{ isSavingCreate ? 'Creating...' : 'Create Product' }}
                  </button>
                </div>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            @if (successMessage) {
              <div class="result result--ok">{{ successMessage }}</div>
            }

            <div class="card">
              <div class="card-hd card-hd--between">
                <span class="card-title">Products</span>
                <span class="count-pill">{{ products().length }} product{{ products().length !== 1 ? 's' : '' }}</span>
              </div>

              <div class="products-list">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th>Default Price</th>
                      <th>Taxable</th>
                      <th>Default Item</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (product of products(); track product.id) {
                      <tr>
                        <td>{{ product.name }}</td>
                        <td>{{ product.sku }}</td>
                        <td>{{ product.description || '-' }}</td>
                        <td>{{ formatPrice(product.defaultPrice) }}</td>
                        <td>{{ product.isTaxable ? 'Yes' : 'No' }}</td>
                        <td>{{ product.defaultItem ? 'Yes' : 'No' }}</td>
                        <td>
                          <button class="btn btn--ghost" type="button" (click)="toggleEdit(product.id)">
                            {{ editOpenId === product.id ? 'Close' : 'Edit' }}
                          </button>
                        </td>
                      </tr>

                      @if (editOpenId === product.id) {
                        <tr>
                          <td colspan="7">
                            <div class="accordion">
                              <div class="form-grid">
                                <label class="field">
                                  <span>Name</span>
                                  <input type="text" [(ngModel)]="editDraft.name" />
                                </label>

                                <label class="field">
                                  <span>SKU</span>
                                  <input type="text" [(ngModel)]="editDraft.sku" />
                                </label>

                                <label class="field field--full">
                                  <span>Description</span>
                                  <input type="text" [(ngModel)]="editDraft.description" />
                                </label>

                                <label class="field">
                                  <span>Default Price</span>
                                  <input type="number" step="0.01" [(ngModel)]="editDraft.defaultPrice" />
                                </label>

                                <label class="field field--checkbox">
                                  <span>Taxable</span>
                                  <input type="checkbox" [(ngModel)]="editDraft.isTaxable" />
                                </label>

                                <label class="field field--checkbox">
                                  <span>Default Item</span>
                                  <input type="checkbox" [(ngModel)]="editDraft.defaultItem" />
                                </label>

                                <div class="actions field--full">
                                  <button class="btn btn--primary" type="button" [disabled]="isSavingEdit" (click)="saveEdit(product.id)">
                                    {{ isSavingEdit ? 'Saving...' : 'Save' }}
                                  </button>
                                  <button class="btn btn--danger" type="button" [disabled]="isDeleting" (click)="deleteProduct(product.id)">
                                    {{ isDeleting ? 'Deleting...' : 'Delete' }}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      }
                    } @empty {
                      <tr>
                        <td class="empty" colspan="7">No products found.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      }
    </div>
  `,
  styles: [`.page{min-height:100vh}.error-section{margin:2rem auto;max-width:720px;padding:1rem 1.25rem;border:1px solid color-mix(in srgb,#ef4444 35%,transparent);border-radius:.8rem;background:color-mix(in srgb,#ef4444 8%,transparent)}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--violet{background:color-mix(in srgb,#8b5cf6 18%,transparent);border:1px solid color-mix(in srgb,#8b5cf6 35%,transparent);color:#a78bfa}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.page-body-inner{display:grid;gap:1rem}.card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-hd--between{display:flex;align-items:center;justify-content:space-between}.card-title{font-weight:600}.count-pill{font-size:.8rem;color:var(--color-muted)}.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.field input{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field--checkbox{align-items:flex-start;justify-content:flex-end}.field--checkbox input{width:18px;height:18px;margin-top:.35rem}.actions{display:flex;gap:.6rem;align-items:center}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.btn--ghost{background:transparent;border-color:var(--color-border);color:var(--color-text)}.btn--danger{background:linear-gradient(135deg,#dc2626,#b91c1c);border-color:#b91c1c;color:#fff}.products-list{padding:1rem;overflow-x:auto}.products-table{width:100%;border-collapse:collapse}.products-table th,.products-table td{border-bottom:1px solid var(--color-border);padding:.7rem .6rem;text-align:left;vertical-align:top}.products-table th{font-size:.78rem;color:var(--color-muted);font-weight:700;text-transform:uppercase;letter-spacing:.03em}.accordion{border:1px solid var(--color-border);border-radius:.6rem;background:color-mix(in srgb,var(--color-panel) 75%,transparent)}.empty{padding:.8rem;color:var(--color-muted)}.result{padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class ProductsPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly productService = inject(ProductService);

  forbidden = false;
  isSavingCreate = false;
  isSavingEdit = false;
  isDeleting = false;

  errorMessage = '';
  successMessage = '';

  readonly products = signal<ProductDto[]>([]);

  editOpenId: number | null = null;
  editDraft: ProductDraft = this.createEmptyDraft(0);

  newProduct: ProductDraft = this.createEmptyDraft(0);

  async ngOnInit(): Promise<void> {
    const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
    if (!isSiteAdmin) {
      this.forbidden = true;
      return;
    }

    await this.loadProducts();
  }

  async createProduct(): Promise<void> {
    this.clearMessages();

    const payload = this.mapDraftToCreate(this.newProduct);
    if (!payload) {
      this.errorMessage = 'Name and SKU are required.';
      return;
    }

    this.isSavingCreate = true;
    try {
      await firstValueFrom(this.productService.create(payload));
      this.successMessage = 'Product created.';
      this.newProduct = this.createEmptyDraft(0);
      await this.loadProducts();
    } catch {
      this.errorMessage = 'Unable to create product.';
    } finally {
      this.isSavingCreate = false;
    }
  }

  toggleEdit(id: number): void {
    if (this.editOpenId === id) {
      this.editOpenId = null;
      this.editDraft = this.createEmptyDraft(0);
      return;
    }

    const product = this.products().find(p => p.id === id);
    if (!product) {
      return;
    }

    this.editOpenId = id;
    this.editDraft = this.mapProductToDraft(product);
  }

  async saveEdit(id: number): Promise<void> {
    this.clearMessages();

    const payload = this.mapDraftToUpdate(this.editDraft);
    if (!payload) {
      this.errorMessage = 'Name and SKU are required.';
      return;
    }

    this.isSavingEdit = true;
    try {
      await firstValueFrom(this.productService.update(id, payload));
      this.successMessage = 'Product updated.';
      await this.loadProducts();
      this.toggleEdit(id);
    } catch {
      this.errorMessage = 'Unable to update product.';
    } finally {
      this.isSavingEdit = false;
    }
  }

  async deleteProduct(id: number): Promise<void> {
    this.clearMessages();
    this.isDeleting = true;

    try {
      await firstValueFrom(this.productService.delete(id));
      this.successMessage = 'Product deleted.';
      await this.loadProducts();
      if (this.editOpenId === id) {
        this.editOpenId = null;
      }
    } catch {
      this.errorMessage = 'Unable to delete product.';
    } finally {
      this.isDeleting = false;
    }
  }

  formatPrice(value?: number | null): string {
    if (value === null || typeof value === 'undefined') {
      return 'N/A';
    }

    return `$${value.toFixed(2)}`;
  }

  private async loadProducts(): Promise<void> {
    this.clearMessages();

    try {
      const products = await firstValueFrom(this.productService.getAll());
      this.products.set(products);
    } catch {
      this.errorMessage = 'Unable to load products.';
    }
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private mapProductToDraft(product: ProductDto): ProductDraft {
    return {
      id: product.id,
      name: product.name,
      sku: product.sku,
      description: product.description ?? '',
      defaultPrice: product.defaultPrice === null || typeof product.defaultPrice === 'undefined'
        ? ''
        : product.defaultPrice.toString(),
      isTaxable: product.isTaxable
      ,defaultItem: product.defaultItem
    };
  }

  private mapDraftToCreate(draft: ProductDraft): ProductCreateDto | null {
    if (!draft.name.trim() || !draft.sku.trim()) {
      return null;
    }

    return {
      name: draft.name.trim(),
      sku: draft.sku.trim(),
      description: draft.description.trim() ? draft.description.trim() : null,
      defaultPrice: this.parseDefaultPrice(draft.defaultPrice),
      isTaxable: draft.isTaxable,
      defaultItem: draft.defaultItem
    };
  }

  private mapDraftToUpdate(draft: ProductDraft): ProductUpdateDto | null {
    if (!draft.name.trim() || !draft.sku.trim()) {
      return null;
    }

    return {
      name: draft.name.trim(),
      sku: draft.sku.trim(),
      description: draft.description.trim() ? draft.description.trim() : null,
      defaultPrice: this.parseDefaultPrice(draft.defaultPrice),
      isTaxable: draft.isTaxable,
      defaultItem: draft.defaultItem
    };
  }

  private parseDefaultPrice(raw: string | number): number | null {
    if (raw === null || typeof raw === 'undefined') {
      return null;
    }

    if (typeof raw === 'number') {
      return Number.isFinite(raw) ? raw : null;
    }

    if (!raw.trim()) {
      return null;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private createEmptyDraft(id: number): ProductDraft {
    return {
      id,
      name: '',
      sku: '',
      description: '',
      defaultPrice: '',
      isTaxable: true,
      defaultItem: false
    };
  }
}
